import React, { useState, useEffect } from 'react';

const AdminOverview = ({ adminId }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/dashboard/overview?admin_id=${adminId}`);
      if (!response.ok) throw new Error('Failed to fetch overview');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-4">
        <p className="text-red-400">Error: {error}</p>
      </div>
    );
  }

  if (!stats) return null;

  const StatCard = ({ title, value, icon, color = 'orange' }) => (
    <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-400 text-sm mb-1">{title}</p>
          <p className={`text-3xl font-bold text-${color}-500`}>{value}</p>
        </div>
        <div className="text-4xl">{icon}</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Users" value={stats.total_users} icon="👥" color="orange" />
        <StatCard title="New (7 Days)" value={stats.new_signups.last_7_days} icon="📈" color="green" />
        <StatCard title="Total Posts" value={stats.total_posts} icon="📝" color="blue" />
        <StatCard title="Images Generated" value={stats.total_images_generated} icon="🖼️" color="purple" />
      </div>

      {/* Subscription Breakdown */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold mb-4 text-orange-500">Subscription Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-400">{stats.subscription_breakdown.trial}</div>
            <div className="text-sm text-gray-500 mt-1">Trial Users</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-500">{stats.subscription_breakdown.basic}</div>
            <div className="text-sm text-gray-500 mt-1">Basic Users</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-500">{stats.subscription_breakdown.pro}</div>
            <div className="text-sm text-gray-500 mt-1">Pro Users</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-500">{stats.subscription_breakdown.enterprise}</div>
            <div className="text-sm text-gray-500 mt-1">Enterprise Users</div>
          </div>
        </div>
      </div>

      {/* Growth Stats */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <h3 className="text-xl font-bold mb-4 text-orange-500">Growth Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-gray-400 text-sm">New Signups (Last 7 Days)</p>
            <p className="text-2xl font-bold text-green-500">{stats.new_signups.last_7_days}</p>
          </div>
          <div>
            <p className="text-gray-400 text-sm">New Signups (Last 30 Days)</p>
            <p className="text-2xl font-bold text-green-500">{stats.new_signups.last_30_days}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOverview;
